"use client";

import Link from "next/link";
import { useState, type RefObject } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { confirmAiPlanChange, type previewAiPlanChange } from "@/lib/actions/ai-plan-change-actions";
import { formatCredits } from "@/lib/ai-credits/monthly-policy";
type Preview = Awaited<ReturnType<typeof previewAiPlanChange>>;
export function AiPlanChangeDialog({
  preview,
  onClose,
  onComplete,
  returnFocusRef
}: {
  preview: Preview;
  onClose: () => void;
  onComplete: () => Promise<void>;
  returnFocusRef?: RefObject<HTMLElement | null>;
}) {
  const [pending, setPending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const amount = `$${(preview.amount / 100).toFixed(2)}`;
  const renewal = new Date(preview.renewalAt * 1000).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC"
  });
  async function confirm() {
    if (pending || submitted) return;
    setPending(true);
    try {
      const result = await confirmAiPlanChange(preview.token);
      setSubmitted(true);
      if (result.status === "complete") await onComplete();else setMessage("Payment needs attention. Your current plan stays active until payment completes.");
    } catch (error) {
      setSubmitted(true);
      setMessage(error instanceof Error ? error.message : "Payment status unavailable. Check billing before trying again.");
    } finally {
      setPending(false);
    }
  }
  return <Dialog open onOpenChange={open => {
    if (!open && !pending) onClose();
  }}>
        <DialogContent returnFocusRef={returnFocusRef} className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
            <DialogTitle>Switch to {preview.targetPlan.name}</DialogTitle>
            <DialogDescription>{preview.currentPlan.name} → {preview.targetPlan.name} · Starts today</DialogDescription>
            <div className="rounded-lg border border-border p-4 space-y-3">
                <p className="flex justify-between gap-4"><span>Due today</span><strong>{amount}</strong></p>
                <p>{formatCredits(preview.targetPlan.units)} credits added today</p>
                <p className="text-sm text-muted-foreground">Then {amount}/month, starting {renewal}.</p>
            </div>
            <p className="text-sm text-muted-foreground">Keep all your paid credits. A new billing month starts today. No refund for the previous billing month. Cancel anytime before renewal.</p>
            {preview.resumesRenewal && <p className="text-sm text-muted-foreground">Monthly renewal restarts when payment succeeds.</p>}
            {message && <p role="alert" className="text-sm text-destructive">{message}</p>}
            {submitted ? <Button asChild><Link href="/editor/settings/billing">Review payment in billing</Link></Button> : <Button disabled={pending} onClick={() => void confirm()}>{pending ? "Confirming…" : `Pay ${amount} & switch to ${preview.targetPlan.name}`}</Button>}
        </DialogContent>
    </Dialog>;
}